import React from 'react';
import { ArrowRight, Cpu } from 'lucide-react';
import AnimatedCard from '../AnimatedCard';
import SectionHeader from '../common/SectionHeader';

const CustomSolutionsWorkflow = () => {
  // Define the workflow steps
  const workflowSteps = [
    { name: "Data Input", color: "bg-blue-500", textColor: "text-blue-100" },
    { name: "Legal Research", model: "Model-1", color: "bg-purple-500", textColor: "text-purple-100" },
    { name: "Legal Reasoning", model: "Model-2", color: "bg-indigo-500", textColor: "text-indigo-100" },
    { name: "Document First Draft", model: "Model-3", color: "bg-cyan-500", textColor: "text-cyan-100" },
    { name: "Firm Playbook Checks", model: "Model-4", color: "bg-green-500", textColor: "text-green-100" },
    { name: "Document Second Draft", model: "Model-3", color: "bg-blue-500", textColor: "text-blue-100" },
    { name: "Attorney Review", color: "bg-amber-500", textColor: "text-amber-100" },
  ];

  return (
    <section className="py-20 bg-[#222538] text-white">
      <div className="container mx-auto px-6">
        <SectionHeader
          title="Work Together"
          highlightedText="How Intelligent AI Systems"
          description="Real innovation happens when multiple AI models are orchestrated into seamless, intelligent workflows — moving data, making decisions, and triggering actions automatically. Our solutions are designed to fit your real-world needs and strategic goals."
        />
        
        <div className="mt-12">
          <AnimatedCard animation="fade-in">
            <div className="flex items-center justify-center mb-8">
              <div className="px-4 py-2 bg-[#1A1D29] rounded-md border border-white/10 flex items-center">
                <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                <h3 className="text-xl font-mono text-gray-200">Task Automation Example</h3>
              </div>
            </div>
            
            {/* Workflow diagram with tech styling */}
            <div className="relative mx-auto max-w-5xl overflow-x-auto pb-6 bg-[#1A1D29] border border-white/10 rounded-lg p-6">
              {/* Tech decorations */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <div className="bg-grid-pattern opacity-10 absolute inset-0"></div>
                {[...Array(10)].map((_, i) => (
                  <div 
                    key={i}
                    className="absolute rounded-full bg-white/5"
                    style={{
                      width: Math.random() * 10 + 5 + 'px',
                      height: Math.random() * 10 + 5 + 'px',
                      top: Math.random() * 100 + '%',
                      left: Math.random() * 100 + '%',
                    }}
                  ></div>
                ))}
              </div>
              
              {/* Circuit lines */}
              <div className="absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 transform -translate-y-1/2"></div>
              
              <div className="flex flex-nowrap items-center justify-start md:justify-center space-x-4 min-w-max relative z-10">
                {workflowSteps.map((step, index) => (
                  <React.Fragment key={index}>
                    {/* Step box */}
                    <div 
                      className={`relative ${step.color} ${step.textColor} rounded-lg shadow-lg p-4 w-44 h-24 flex flex-col justify-center items-center text-center transition-all hover:scale-105 border border-white/20`}
                    >
                      <div className="font-mono font-medium">{step.name}</div>
                      {step.model && <div className="text-xs mt-1 opacity-80 font-mono">{step.model}</div>}
                      
                      {/* Step indicator */}
                      <div className="absolute -top-3 -right-3 w-6 h-6 bg-[#1A1D29] rounded-full flex items-center justify-center border border-white/20 text-xs font-mono">
                        {index + 1}
                      </div>
                    </div>
                    
                    {/* Arrow between steps */}
                    {index < workflowSteps.length - 1 && (
                      <div className="flex-shrink-0 relative">
                        <ArrowRight className="h-6 w-6 text-gray-400" />
                        <div className="absolute -bottom-2 left-1/2 w-1 h-1 bg-blue-400 rounded-full animate-pulse transform -translate-x-1/2"></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Code snippet hint */}
            <div className="mt-6 text-center">
              <code className="inline-block bg-[#1A1D29] px-3 py-1 rounded text-xs text-gray-400 font-mono">
                <span className="text-blue-400">orchestrator</span>.<span className="text-green-400">connect</span>(<span className="text-amber-400">models</span>).<span className="text-purple-400">configureWorkflow</span>()
              </code>
            </div>
          </AnimatedCard>
        </div>
      </div>
    </section>
  );
};

export default CustomSolutionsWorkflow;
